/**
 * TagInputEditor
 * Configuration for tag-input control
 */

import React, { useCallback } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';

interface TagInputConfig {
  placeholder?: string;
  defaultTags?: string[];
  allowDuplicates?: boolean;
  separator?: 'comma' | 'space' | 'enter';
  maxTags?: number;
  minTagLength?: number;
  maxTagLength?: number;
  allowSpaces?: boolean;
}

function renderTagInputEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config || {}) as TagInputConfig;

  const defaultTags = config.defaultTags || [];
  const separator = config.separator || 'comma';
  const allowDuplicates = config.allowDuplicates ?? false;
  const allowSpaces = config.allowSpaces ?? true;

  const handleDefaultTagsChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const tags = e.currentTarget.value
        .split('\n')
        .map((tag) => tag.trim())
        .filter((tag) => tag);
      onConfigChange('defaultTags', tags);
    },
    [onConfigChange],
  );

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Tag Input Configuration</h4>

      <div className="af-properties-tab__group">
        <label htmlFor="placeholder-input" className="af-properties-tab__label">
          Placeholder Text
        </label>
        <input
          id="placeholder-input"
          type="text"
          value={config.placeholder || ''}
          onChange={(e) => onConfigChange('placeholder', e.target.value)}
          placeholder="Enter tags..."
          className="af-properties-tab__input"
          aria-label="Placeholder text"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="separator-select" className="af-properties-tab__label">
          Tag Separator
        </label>
        <select
          id="separator-select"
          value={separator}
          onChange={(e) => onConfigChange('separator', e.target.value)}
          className="af-properties-tab__select"
        >
          <option value="comma">Comma (,)</option>
          <option value="space">Space</option>
          <option value="enter">Enter Key</option>
        </select>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={allowDuplicates}
            onChange={(e) => onConfigChange('allowDuplicates', e.target.checked)}
          />
          Allow Duplicate Tags
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={allowSpaces}
            onChange={(e) => onConfigChange('allowSpaces', e.target.checked)}
          />
          Allow Spaces in Tags
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="max-tags-input" className="af-properties-tab__label">
          Maximum Tags (0 = unlimited)
        </label>
        <input
          id="max-tags-input"
          type="number"
          value={config.maxTags || 0}
          onChange={(e) => onConfigChange('maxTags', Number(e.target.value))}
          min="0"
          className="af-properties-tab__input"
          aria-label="Maximum tags"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="min-tag-length-input" className="af-properties-tab__label">
          Minimum Tag Length
        </label>
        <input
          id="min-tag-length-input"
          type="number"
          value={config.minTagLength || 1}
          onChange={(e) => onConfigChange('minTagLength', Number(e.target.value))}
          min="1"
          className="af-properties-tab__input"
          aria-label="Minimum tag length"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="max-tag-length-input" className="af-properties-tab__label">
          Maximum Tag Length
        </label>
        <input
          id="max-tag-length-input"
          type="number"
          value={config.maxTagLength || 50}
          onChange={(e) => onConfigChange('maxTagLength', Number(e.target.value))}
          min="1"
          className="af-properties-tab__input"
          aria-label="Maximum tag length"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="default-tags-textarea" className="af-properties-tab__label">
          Default Tags (one per line)
        </label>
        <textarea
          id="default-tags-textarea"
          value={defaultTags.join('\n')}
          onChange={handleDefaultTagsChange}
          placeholder="tag1&#10;tag2&#10;tag3"
          rows={4}
          className="af-properties-tab__textarea"
          aria-label="Default tags"
        />
      </div>
    </div>
  );
}

export const TagInputEditor: PropertyEditor = {
  type: 'tag-input',
  label: 'Tag Input',
  description: 'Configure tag input with validation and defaults',
  render: renderTagInputEditor,
};
